import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHeaderCell,
  CTableRow,
  CButton,
  CInputGroup,
  CInputGroupText,
  CFormInput,
  CPagination,
  CFormSelect,
} from "@coreui/react";
import { cilPencil, cilTrash, cilCloudDownload } from "@coreui/icons"; // Added download icon
import CIcon from "@coreui/icons-react";
import useApi from "../../../config/useApi"; // Assuming the useApi hook is reused
import ProductModal from "./ProductsModal"; // Assuming you'll create a modal for editing product
import { CSVLink } from "react-csv"; // Importing CSVLink component from react-csv

const ProductTable = () => {
  const { Get, Delete } = useApi();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(5);
  const [categories, setCategories] = useState([]);

  // Fetch products and categories
  const fetchProducts = async () => {
    try {
      const data = await Get("products");
      setProducts(data.results); // Assuming the API returns an array under `results`
      setFilteredProducts(data.results);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await Get("categories");
      setCategories(data.results); // Assuming the API returns an array under `results`
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const handleDelete = async (id) => {
    try {
      await Delete("products", id);
      fetchProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    const filtered = products.filter(
      (product) =>
        product.name.toLowerCase().includes(query.toLowerCase()) ||
        product.product_price.toLowerCase().includes(query.toLowerCase()) ||
        product.category_name.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  const handleProductsPerPageChange = (e) => {
    setProductsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const currentDataRangeStart = indexOfFirstProduct + 1;
  const currentDataRangeEnd = Math.min(indexOfLastProduct, filteredProducts.length);

  // Prepare CSV Data
  const csvData = [
    ["Product Name", "Price", "Category", "Description"], // CSV header
    ...filteredProducts.map((product) => [
      product.name,
      product.product_price,
      product.category_name,
      product.description,
    ]),
  ];

  return (
    <div className="product-table-container">
      <CCard>
        <CCardHeader>
          <h4>Product Management</h4>
        </CCardHeader>

        <CCardBody>
          {/* Search, Add Product, and Export Controls */}
          <div className="table-header">
            <CButton
              color="primary"
              onClick={() => {
                setSelectedProduct(null);
                setShowModal(true);
              }}
              className="add-product-btn"
            >
              Add Product
            </CButton>

            <CInputGroup className="search-box">
              <CInputGroupText>
                <span role="img" aria-label="search">
                  🔍
                </span>
              </CInputGroupText>
              <CFormInput
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search by product name, price, or category"
              />
            </CInputGroup>

            <CFormSelect
              aria-label="Items per page"
              value={productsPerPage}
              onChange={handleProductsPerPageChange}
              className="items-per-page"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={30}>30 per page</option>
            </CFormSelect>

            {/* CSV Export Button */}
            <CSVLink
              data={csvData}
              filename={"products_data.csv"}
              className="btn btn-success export-btn"
              target="_blank"
            >
              <CIcon icon={cilCloudDownload} style={{ color: "white" }} size="lg" />{" "}
              <a style={{ color: "white" }}>Export</a>
            </CSVLink>
          </div>

          {/* Table Container */}
          <div className="table-responsive">
            <CTable striped hover responsive="sm" className="product-table">
              <thead>
                <tr>
                  <CTableHeaderCell scope="col">No</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Product Name</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Price</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Category</CTableHeaderCell>
                  <CTableHeaderCell scope="col">Actions</CTableHeaderCell>
                </tr>
              </thead>
              <CTableBody>
                {currentProducts.map((product, index) => (
                  <CTableRow key={product.id}>
                    <CTableDataCell>{indexOfFirstProduct + index + 1}</CTableDataCell>
                    <CTableDataCell>{product.name}</CTableDataCell>
                    <CTableDataCell>{product.product_price}</CTableDataCell>
                    <CTableDataCell>{product.category}</CTableDataCell>
                    <CTableDataCell>
                      <CButton
                        color="warning"
                        onClick={() => handleEdit(product)}
                        className="action-btn"
                      >
                        <CIcon icon={cilPencil} size="lg" />
                      </CButton>
                      <CButton
                        color="danger"
                        onClick={() => handleDelete(product.id)}
                        className="action-btn"
                      >
                        <CIcon icon={cilTrash} size="lg" />
                      </CButton>
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>

          {/* Pagination */}
          <div className="pagination-container">
            <span>
              Showing {currentDataRangeStart} to {currentDataRangeEnd} of {filteredProducts.length}
            </span>
          </div>

          <CPagination
            activePage={currentPage}
            pages={totalPages}
            onActivePageChange={paginate}
            className="pagination-bar"
            align="center"
          >
            {/* Previous Button */}
            <CButton
              color="secondary"
              disabled={currentPage === 1} // Disable if we're on the first page
              onClick={() => paginate(currentPage - 1)}
            >
              ◀
            </CButton>
            &nbsp;

            {[...Array(totalPages)].map((_, index) => (
              <CButton
                key={index}
                color={currentPage === index + 1 ? "primary" : "light"}
                onClick={() => paginate(index + 1)}
              >
                {index + 1}
              </CButton>
            ))}

            &nbsp;
            <CButton
              color="secondary"
              disabled={currentPage === totalPages} // Disable if we're on the last page
              onClick={() => paginate(currentPage + 1)}
            >
              ▶
            </CButton>
          </CPagination>
        </CCardBody>
      </CCard>

      {/* Product Modal */}
      {showModal && (
        <ProductModal
          show={showModal}
          onClose={() => setShowModal(false)}
          product={selectedProduct}
          onRefresh={fetchProducts}
          categories={categories}
        />
      )}
    </div>
  );
};

export default ProductTable;
